/*
 * AutorizacionOcSolicitud.jsx — oct-2026
 *
 * UNA sola decisión del Oficial de Cumplimiento por solicitud de un cliente en grado de
 * riesgo ALTO, usada en las DOS pantallas que ya existen (no se crea ninguna nueva):
 *
 *   • `variant="panel"` → dentro del detalle: «Originación + Consulta SIC» (junto al aviso
 *     rojo de PLD) y «Solicitudes de crédito» (diálogo de la solicitud).
 *   • `variant="row"`   → etiqueta «Requiere autorización OC» en la propia lista de
 *     Solicitudes de crédito; al pulsarla abre el mismo bloque (con el EBR) para decidir.
 *     Es la única vía de entrada de la Oficial, que tiene la solicitud en SOLO LECTURA.
 *
 * Reglas: carta PDF obligatoria al autorizar, motivo ≥ 20 caracteres en ambos casos, botones
 * solo para el rol `oficial_cumplimiento` (el backend responde 403 a los demás) y, una vez
 * decidida, en ambas pantallas se ve el resultado y los botones desaparecen.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ShieldCheck, ShieldOff, Loader2, Upload, FileDown, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SolicitudPldPanel } from "@/components/solicitudes/SolicitudPldPanel";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const MOTIVO_MIN = 20;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function CartaBoton({ dec }) {
  const [bajando, setBajando] = useState(false);
  if (!dec?.carta_doc_id) return null;
  const abrir = async () => {
    setBajando(true);
    try {
      const { data } = await api.post(`/documentos/${dec.carta_doc_id}/signed-url`);
      window.open(`${BACKEND_URL}${data.url}`, "_blank", "noopener");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBajando(false);
    }
  };
  return (
    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={abrir}
      disabled={bajando} data-testid={`oc-carta-descargar-${dec.cliente_id}`}>
      {bajando ? <Loader2 className="mr-1 h-3 w-3 animate-spin" />
               : <FileDown className="mr-1 h-3 w-3" />}
      {dec.carta_nombre || "Carta PDF"}
    </Button>
  );
}

function Resultado({ dec }) {
  const si = dec.decision === "autorizado";
  return (
    <div data-testid={`oc-decision-${dec.cliente_id}`}
      className={"rounded-md border p-3 text-sm " + (si
        ? "border-emerald-200 bg-emerald-50/70" : "border-rose-200 bg-rose-50/70")}>
      <div className="flex flex-wrap items-center gap-2">
        {si ? <ShieldCheck className="h-4 w-4 text-emerald-700" />
            : <ShieldOff className="h-4 w-4 text-rose-700" />}
        <b className={si ? "text-emerald-900" : "text-rose-900"}>
          {si ? "Autorizado por OC" : "No autorizado por OC"}
        </b>
        <span className="text-xs text-slate-600">
          · {dec.autorizado_por || "—"} · {dec.fecha || String(dec.ts || "").slice(0, 10)}
        </span>
        <span className="ml-auto"><CartaBoton dec={dec} /></span>
      </div>
      <p className="mt-1 text-[13px] text-slate-700">Motivo: {dec.motivo}</p>
      {!si && (
        <p className="mt-1 text-[12px] font-medium text-rose-800">
          La solicitud queda bloqueada: no puede enviarse al Comité.
        </p>
      )}
    </div>
  );
}

function Decidir({ persona, solicitudId, onHecho }) {
  const [modo, setModo] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [cartaId, setCartaId] = useState("");
  const [cartaNombre, setCartaNombre] = useState("");
  const [subiendo, setSubiendo] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const fileRef = useRef(null);
  const cid = persona.cliente_id;

  const cerrar = () => {
    setModo(null); setMotivo(""); setCartaId(""); setCartaNombre("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const subir = async (file) => {
    if (!file) return;
    if (!/pdf/i.test(file.type || "") && !/\.pdf$/i.test(file.name || "")) {
      toast.error("La carta de autorización debe ser un archivo PDF.");
      return;
    }
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo_documento", "Carta de autorización (riesgo ALTO)");
      fd.append("categoria", "override");
      fd.append("clasificacion", "Soporte");
      fd.append("cliente_id", cid);
      fd.append("referencia_id", solicitudId);
      const { data } = await api.post("/documentos/upload", fd);
      setCartaId(data.id);
      setCartaNombre(data.nombre_archivo || file.name);
      toast.success("Carta PDF cargada y cifrada.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubiendo(false);
    }
  };

  const enviar = async (autoriza) => {
    setGuardando(true);
    try {
      const ruta = autoriza ? "autorizar-riesgo-alto" : "no-autorizar-riesgo-alto";
      await api.post(`/solicitudes/${solicitudId}/${ruta}`, {
        cliente_id: cid, motivo: motivo.trim(),
        ...(autoriza ? { carta_doc_id: cartaId } : {}),
      });
      toast.success(autoriza
        ? "Operación autorizada. La solicitud puede seguir al Comité."
        : "Operación NO autorizada. La solicitud queda bloqueada con tu motivo.");
      cerrar();
      onHecho && onHecho();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setGuardando(false);
    }
  };

  const motivoOk = motivo.trim().length >= MOTIVO_MIN;

  return (
    <>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button size="sm" className="bg-rose-700 text-white hover:bg-rose-800"
          onClick={() => setModo("si")} data-testid={`oc-autorizar-btn-${cid}`}>
          <ShieldCheck className="mr-2 h-4 w-4" /> Autorizar como Oficial de Cumplimiento
        </Button>
        <Button size="sm" variant="outline"
          className="border-slate-300 text-slate-700 hover:bg-slate-100"
          onClick={() => setModo("no")} data-testid={`oc-no-autorizar-btn-${cid}`}>
          <ShieldOff className="mr-2 h-4 w-4" /> No autorizar
        </Button>
      </div>

      <Dialog open={!!modo} onOpenChange={(v) => !v && cerrar()}>
        <DialogContent className="sm:max-w-lg"
          data-testid={modo === "no" ? "oc-no-autorizar-dialog" : "oc-autorizar-dialog"}>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {modo === "no" ? "No autorizar la operación" : "Autorizar la operación"} — cliente de riesgo ALTO
            </DialogTitle>
            <DialogDescription>
              {persona.cliente || "Cliente"} · la decisión vale <b>solo para esta solicitud</b>,
              es <b>única</b> (no se puede cambiar después) y queda en la bitácora y en el
              expediente del cliente.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {modo === "si" && (
              <div>
                <Label className="mb-1 block text-xs font-medium text-slate-700">
                  Carta de autorización firmada (PDF, obligatoria)
                </Label>
                <div className="flex items-center gap-2">
                  <Input ref={fileRef} type="file" accept="application/pdf,.pdf"
                    disabled={subiendo || !!cartaId} className="h-9"
                    onChange={(e) => subir(e.target.files?.[0] || null)}
                    data-testid="oc-carta-file" />
                  {subiendo && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
                </div>
                {cartaId && (
                  <p className="mt-1 flex items-center gap-1 text-xs text-emerald-700"
                     data-testid="oc-carta-ok">
                    <Upload className="h-3 w-3" /> Carta cargada: <b>{cartaNombre}</b>
                  </p>
                )}
              </div>
            )}
            <div>
              <Label className="mb-1 block text-xs font-medium text-slate-700">
                Motivo {modo === "no" ? "de la negativa" : "de la autorización"} (mínimo {MOTIVO_MIN} caracteres)
              </Label>
              <Textarea rows={4} value={motivo} onChange={(e) => setMotivo(e.target.value)}
                placeholder={modo === "no"
                  ? "Ej. el origen de los recursos no quedó acreditado y el perfil transaccional no corresponde al ingreso declarado."
                  : "Fundamento: mitigantes aplicados, acuerdo del comité PLD, verificación de origen de recursos, etc."}
                data-testid={modo === "no" ? "oc-no-autorizar-motivo" : "oc-motivo"} />
              <p className="mt-1 text-[11px] text-slate-500" data-testid="oc-motivo-contador">
                {motivo.trim().length} / {MOTIVO_MIN} caracteres
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="ghost" onClick={cerrar} disabled={guardando}
              data-testid="oc-cancelar">Cancelar</Button>
            {modo === "si" ? (
              <Button size="sm" className="bg-rose-700 text-white hover:bg-rose-800"
                disabled={!cartaId || !motivoOk || guardando} onClick={() => enviar(true)}
                data-testid="oc-confirmar">
                {guardando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                           : <ShieldCheck className="mr-2 h-4 w-4" />}
                Autorizar operación
              </Button>
            ) : (
              <Button size="sm" variant="outline"
                className="border-rose-300 text-rose-700 hover:bg-rose-50"
                disabled={!motivoOk || guardando} onClick={() => enviar(false)}
                data-testid="oc-no-autorizar-confirmar">
                {guardando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                           : <ShieldOff className="mr-2 h-4 w-4" />}
                No autorizar y bloquear
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Bloque({ solicitud, estado, esOC, recargar, onDone, conEbr }) {
  const personas = estado?.personas || [];
  const vivas = personas.filter((p) => p.decision || p.requiere_autorizacion);
  if (!vivas.length) return null;
  const hecho = () => { recargar(); onDone && onDone(); };
  return (
    <div className="space-y-3" data-testid={`oc-bloque-${solicitud?.id}`}>
      {/* El EBR (grado consolidado + los 5 factores) es insumo de la decisión: se muestra
          solo al Oficial de Cumplimiento. El Comité no lo ve ni decide sobre él. */}
      {conEbr && esOC && <SolicitudPldPanel solicitud={solicitud} />}
      {vivas.map((p) => (
        <div key={`oc-${p.cliente_id}`}>
          {p.decision ? (
            <Resultado dec={p.decision} />
          ) : esOC ? (
            <div className="rounded-md border border-rose-200 bg-rose-50/70 p-3"
                 data-testid={`oc-pendiente-${p.cliente_id}`}>
              <p className="flex items-start gap-2 text-sm font-medium text-rose-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  {p.cliente || "Cliente"} en grado de riesgo <b>ALTO</b>: requiere tu
                  autorización para operar.
                </span>
              </p>
              <p className="text-[12px] text-rose-800">
                Autoriza con carta firmada en PDF y motivo, o no autorices (la solicitud queda
                bloqueada y el ejecutivo verá tu motivo).
              </p>
              <Decidir persona={p} solicitudId={solicitud.id} onHecho={hecho} />
            </div>
          ) : (
            <p className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-900"
               data-testid={`oc-requiere-aviso-${p.cliente_id}`}>
              <AlertTriangle className="h-4 w-4 shrink-0" />
              Requiere autorización del Oficial de Cumplimiento: {p.cliente || "el cliente"} está
              en grado de riesgo ALTO. No se puede enviar al Comité hasta que autorice.
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

export function AutorizacionOcSolicitud({ solicitud, onDone, variant = "panel" }) {
  const { user } = useAuth();
  const esOC = user?.role === "oficial_cumplimiento";
  const [estado, setEstado] = useState(null);
  const [abierto, setAbierto] = useState(false);
  const sid = solicitud?.id;

  const recargar = useCallback(() => {
    if (!sid) return;
    api.get(`/solicitudes/${sid}/autorizacion-riesgo-alto`)
      .then((r) => setEstado(r.data))
      .catch(() => {});
  }, [sid]);

  const necesitaEstado = variant === "panel" || abierto;
  useEffect(() => { if (necesitaEstado) recargar(); }, [necesitaEstado, recargar]);

  if (!sid) return null;

  // ── Lista de Solicitudes de crédito: etiqueta + entrada al bloque (solo la Oficial).
  if (variant === "row") {
    const dec = solicitud.autorizacion_oc;
    if (!esOC || (!solicitud.requiere_autorizacion_oc && !dec)) return null;
    const pendiente = !!solicitud.requiere_autorizacion_oc;
    return (
      <>
        <button type="button" onClick={() => setAbierto(true)}
          data-testid={pendiente ? `oc-requiere-badge-${sid}` : `oc-decidida-badge-${sid}`}
          title={pendiente ? "Requiere tu autorización como Oficial de Cumplimiento"
                           : "Ver la decisión registrada"}
          className={"inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold transition-colors "
            + (pendiente
              ? "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
              : dec?.decision === "autorizado"
                ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                : "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100")}>
          {pendiente ? <AlertTriangle className="h-3 w-3" />
                     : dec?.decision === "autorizado" ? <ShieldCheck className="h-3 w-3" />
                                                      : <ShieldOff className="h-3 w-3" />}
          {pendiente ? "Requiere autorización OC"
                     : dec?.decision === "autorizado" ? "Autorizado por OC" : "No autorizado por OC"}
        </button>
        <Dialog open={abierto} onOpenChange={(v) => !v && setAbierto(false)}>
          <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-2xl"
            data-testid="oc-row-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">
                Autorización del Oficial de Cumplimiento — {solicitud.cliente || "solicitud"}
              </DialogTitle>
              <DialogDescription>
                Evaluación de riesgo (EBR) de la solicitud y decisión única de cumplimiento.
                La solicitud en sí se consulta en solo lectura.
              </DialogDescription>
            </DialogHeader>
            {!estado ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              </div>
            ) : (
              <Bloque solicitud={solicitud} estado={estado} esOC={esOC} conEbr
                recargar={recargar} onDone={onDone} />
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // ── Detalle (Originación + Consulta SIC y diálogo de Solicitudes de crédito).
  if (!estado) return null;
  return (
    <Bloque solicitud={solicitud} estado={estado} esOC={esOC} conEbr={variant === "panel-ebr"}
      recargar={recargar} onDone={onDone} />
  );
}

export default AutorizacionOcSolicitud;
